// Search, over the page you are on (Kareem, 4 Nov): the sidebar's Search, "/" or Cmd/Ctrl+K open it. One box finds
// everything you can see, grouped by kind (spaces, tracks, activities, meetings, reminders, files and the knowledge
// base, research insights, people), from the same search the Search page uses, so partner and space rules are the
// server's. Arrow keys move, Enter opens, Escape closes and focus goes back where it was. With no words it is the
// quick jump list (pages and spaces). "See all results" opens the Search page (tags, kinds, saved filters); "Ask the
// assistant" sends the words to the assistant. On a phone it is a full-screen sheet.
import { useActivityDrawer } from '../shell/Dock';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { shownSpaceColor } from '@dpw/shared';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useReminders, useSpaces } from '../lib/queries';
import { scrim, spring } from '../lib/motion';
import { isTyping } from '../lib/keys';
import { usePhone } from '../lib/media';
import { IconBell, IconBook, IconCalendar, IconChat, IconClock, IconDoc, IconFolder, IconGear, IconHome, IconInbox, IconMail, IconPlus, IconSearch, IconTrack, TypeIcon } from './icons';
import { Orb } from './motion';
import { useAssistantPanel } from '../assistant/Panel';
import { useFolders } from '../kb/hooks';
import { fromResult, matchesWords, modalGroups, rememberSearch, whenText, type ModalGroup, type ModalItem } from '../search/model';

interface Cmd { id: string; group: string; groupNote?: string; label: string; sub?: string; hint?: string; icon: ReactNode; run: () => void }

const PAGES: [string, string, ReactNode][] = [
  ['Today', '/', <IconHome key="h" />], ['Calendar', '/calendar', <IconCalendar key="c" />], ['Meetings', '/meetings', <IconChat key="m" />], ['Reminders', '/reminders', <IconClock key="r" />],
  ['Assistant', '/assistant', <Orb key="o" size={12} />], ['Requests', '/requests', <IconInbox key="i" />], ['Notifications', '/notifications', <IconBell key="b" />],
  ['Knowledge base', '/folders', <IconFolder key="f" />], ['Research Center', '/knowledge', <IconBook key="k" />], ['Search page', '/search', <IconSearch key="s" />], ['Settings', '/settings', <IconGear key="g" />],
];

const ACT_TYPES = ['meeting', 'workshop', 'deliverable', 'task'] as const;
type ActType = typeof ACT_TYPES[number];
const isActType = (t: string | null): t is ActType => !!t && (ACT_TYPES as readonly string[]).includes(t);

/** The small icon in front of a row, by what it is. */
function itemIcon(i: ModalItem): ReactNode {
  if ((i.kind === 'activity') && isActType(i.activityType)) return <TypeIcon type={i.activityType} />;
  switch (i.kind) {
    case 'space': return <span className={`dt ${i.activityType ?? ''}`} />;
    case 'track': return <IconTrack />;
    case 'minutes': return <IconChat />;
    case 'email': return <IconMail />;
    case 'reminder': return <IconClock />;
    case 'folder': return <IconFolder />;
    case 'file': return <IconDoc />;
    case 'insight': return <IconBook />;
    case 'person': return <span className="palav" aria-hidden="true">{i.title.replace(/^(Dr|Eng|Mr|Mrs|Ms)\.?\s+/i, '').split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('')}</span>;
    default: return <IconSearch />;
  }
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen(o => !o); }
      // "/" opens it from anywhere you are not typing and no other dialog is open.
      else if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !e.defaultPrevented && !isTyping(e.target) && !document.querySelector('[aria-modal="true"]')) {
        e.preventDefault(); setOpen(true);
      }
      // Escape closes it wherever focus is: right after opening, focus can still be on the page for a frame,
      // and a click on the modal's edge moves focus off the input.
      else if (e.key === 'Escape') setOpen(o => (o ? false : o));
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('dpw:palette', onOpen);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('dpw:palette', onOpen); };
  }, []);
  return <AnimatePresence>{open && <Palette onClose={() => setOpen(false)} />}</AnimatePresence>;
}

/** Open search from a button (the sidebar's Search, the phone's search buttons). */
export const openPalette = () => window.dispatchEvent(new Event('dpw:palette'));

function Palette({ onClose }: { onClose: () => void }) {
  const nav = useNavigate();
  const phone = usePhone();
  const spaces = useSpaces();
  const reminders = useReminders('mine');
  const folders = useFolders();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const term = q.trim();
  const [debounced, setDebounced] = useState('');
  useEffect(() => { const h = window.setTimeout(() => setDebounced(term), 140); return () => window.clearTimeout(h); }, [term]);
  // The Search page's own query (same key), so its results are already there when "See all results" opens it.
  const found = useQuery({ queryKey: ['search-all', debounced, 'all', ''], queryFn: () => api.search.all(debounced, {}), enabled: debounced.length >= 2, staleTime: 15_000 });

  useLayoutEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    input.current?.focus();
    return () => before?.focus?.({ preventScroll: true });
  }, []);

  /**
   * Every "open this activity" from search goes through here (an activity, its minutes or emails, a reminder on it).
   * The activity opens in its panel beside the page you were on (6.2); a result that points into the activity (a tab,
   * a moment) opens its page there.
   */
  const acts = useActivityDrawer();
  const openActivity = (id: string, query = '') => (query ? nav(`/activities/${id}${query}`) : acts.open(id));
  const go = (to: string) => () => {
    if (term.length >= 2) rememberSearch(term);
    onClose();
    const act = /^\/activities\/([^/?#]+)(\?[^#]*)?$/.exec(to);
    if (act) openActivity(act[1]!, act[2] ?? ''); else nav(to);
  };
  const panel = useAssistantPanel();
  const searching = term.length >= 2 && (debounced !== term || found.isFetching) && !found.data;
  const cmds = useMemo<Cmd[]>(() => {
    const out: Cmd[] = [];
    if (!term) {
      for (const [label, to, icon] of PAGES) out.push({ id: `p:${to}`, group: 'Go to', label, icon, run: go(to) });
      for (const s of spaces.data ?? []) out.push({ id: `s:${s.id}`, group: 'Spaces', label: s.name, icon: <span className={`dt ${shownSpaceColor(s.color)}`} />, run: go(`/spaces/${s.id}`) });
      out.push({ id: 'n:reminder', group: 'Start', label: 'Add a reminder', icon: <IconPlus />, run: go('/reminders?add=1') });
      return out;
    }
    // What the app already has (spaces, folders, your reminders: each list is only what you can see), then the server's.
    const items: ModalItem[] = [];
    for (const s of spaces.data ?? []) if (matchesWords(s.name, term)) {
      items.push({ key: `space:${s.id}`, group: 'Spaces', title: s.name, where: `Space · ${s.tracks} ${s.tracks === 1 ? 'track' : 'tracks'}, ${s.open} open${s.clientName ? ` · ${s.clientName}` : ''}`, href: `/spaces/${s.id}`, when: null, kind: 'space', activityType: shownSpaceColor(s.color) });
    }
    for (const r of reminders.data ?? []) if (!r.doneAt && matchesWords(`${r.text} ${r.activity?.title ?? ''}`, term)) {
      items.push({ key: `reminder:${r.id}`, group: 'Reminders', title: r.text, where: r.activity ? `Reminder · ${r.activity.title}` : 'Reminder', href: r.activity ? `/activities/${r.activity.id}` : '/reminders', when: r.dueAt, kind: 'reminder', activityType: null });
    }
    for (const f of folders.data ?? []) if (matchesWords(`${f.name} ${f.description}`, term)) {
      items.push({ key: `folder:${f.id}`, group: 'Files and knowledge base', title: f.name, where: `Folder · ${f.spaceName ?? 'Team-wide'} · ${f.fileCount} ${f.fileCount === 1 ? 'file' : 'files'}`, href: `/folders/${f.id}`, when: null, kind: 'folder', activityType: null });
    }
    if (debounced === term) for (const r of found.data?.items ?? []) items.push(fromResult(r));
    for (const g of modalGroups(items)) {
      const note = g.total > g.items.length ? `${g.items.length} of ${g.total}` : undefined;
      for (const i of g.items) {
        const when = i.when ? whenText({ kind: i.kind === 'activity' ? 'activity' : 'track', when: i.when }) : '';
        out.push({ id: i.key, group: g.group satisfies ModalGroup, groupNote: note, label: i.title, sub: i.where, hint: when || undefined, icon: itemIcon(i), run: go(i.href) });
      }
    }
    for (const [label, to, icon] of PAGES) if (matchesWords(label, term)) out.push({ id: `p:${to}`, group: 'Go to', label, icon, run: go(to) });
    out.push({ id: 'ask', group: 'Assistant', label: `Ask the assistant: ${term}`, icon: <Orb size={12} />, run: () => { onClose(); panel.open({ prompt: term, send: true, fresh: true }); } });
    if (term.length >= 2) out.push({ id: 'search', group: 'More', label: `See all results for ${term}`, hint: 'Tags, kinds, saved filters', icon: <IconSearch />, run: go(`/search?q=${encodeURIComponent(term)}`) });
    return out;
  }, [term, debounced, spaces.data, reminders.data, folders.data, found.data]);

  useEffect(() => setSel(0), [term, debounced, found.data]);
  useEffect(() => { list.current?.querySelector<HTMLElement>(`[data-i="${sel}"]`)?.scrollIntoView({ block: 'nearest' }); }, [sel]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(s => Math.min(cmds.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(s => Math.max(0, s - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); cmds[sel]?.run(); }
    else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
  };

  let lastGroup = '';
  const results = term ? cmds.filter(c => !['Go to', 'Assistant', 'More'].includes(c.group)).length : -1;
  return (
    <>
      <motion.div className="scrim pal" variants={scrim} initial="initial" animate="animate" exit="exit" onClick={onClose} />
      <motion.div className={`palette${phone ? ' phfull' : ''}`} role="dialog" aria-modal="true" aria-label="Search"
        initial={phone ? { opacity: 0, y: 24 } : { opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1, transition: spring.snappy }}
        exit={{ opacity: 0, scale: phone ? 1 : 0.98, transition: { duration: 0.12 } }}>
        <div className="palin">
          <IconSearch />
          <input ref={input} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey} placeholder="Search spaces, activities, meetings, files, people..."
            aria-label="Search everything" role="combobox" aria-expanded="true" aria-controls="pal-list" aria-activedescendant={cmds[sel] ? `pal-${sel}` : undefined}
            autoComplete="off" enterKeyHint="search" />
          {phone ? <button type="button" className="palx" onClick={onClose}>Cancel</button> : <kbd>Esc</kbd>}
        </div>
        <div className="pallist" id="pal-list" role="listbox" aria-label="Results" ref={list}>
          {cmds.map((c, i) => {
            const head = c.group !== lastGroup ? <div className="palg" key={`g-${c.group}`} role="presentation">{c.group}{c.groupNote && <span className="paln">{c.groupNote}</span>}</div> : null;
            lastGroup = c.group;
            const searchRow = i === cmds.findIndex(x => x.group === 'Go to' || x.group === 'Assistant');
            return [
              searchRow && term && results === 0 && !searching ? <div key="none" className="xs mut palnone" role="status">Nothing you can see matches "{term}".</div> : null,
              searchRow && searching ? <div key="busy" className="xs mut palnone" role="status">Searching...</div> : null,
              head,
              <button key={c.id} id={`pal-${i}`} data-i={i} type="button" role="option" aria-selected={i === sel} className={`palit${i === sel ? ' on' : ''}${c.sub ? ' two' : ''}`}
                data-group={c.group} onMouseMove={() => setSel(i)} onClick={c.run}>
                <span className="pic">{c.icon}</span>
                <span className="pl1"><span className="plt">{c.label}</span>{c.sub && <span className="pls xs mut">{c.sub}</span>}</span>
                {c.hint && <span className="xs mut palh">{c.hint}</span>}
              </button>];
          })}
        </div>
        {!phone && <div className="palfoot xs mut" aria-hidden="true"><span><kbd>↑</kbd><kbd>↓</kbd> move</span><span><kbd>Enter</kbd> open</span><span><kbd>Esc</kbd> close</span></div>}
      </motion.div>
    </>
  );
}
